/**
 * Centralized application logger.
 *
 * Log level is controlled by the VITE_LOG_LEVEL environment variable.
 * Supported levels (in order of verbosity):
 *   'debug'  → all messages
 *   'info'   → info, warn, error
 *   'warn'   → warn, error
 *   'error'  → error only
 *   'silent' → nothing
 *
 * Example .env value:
 *   VITE_LOG_LEVEL=warn
 */

const LEVELS = {
   debug: 0,
   info: 1,
   warn: 2,
   error: 3,
   silent: 4,
};

const configuredLevel = LEVELS[import.meta.env.VITE_LOG_LEVEL?.toLowerCase()] ?? LEVELS.debug;

const isEnabled = level => LEVELS[level] >= configuredLevel;

export const logger = {
   debug: (...args) => isEnabled('debug') && console.debug(...args),
   log: (...args) => isEnabled('debug') && console.log(...args),
   info: (...args) => isEnabled('info') && console.info(...args),
   warn: (...args) => isEnabled('warn') && console.warn(...args),
   error: (...args) => isEnabled('error') && console.error(...args),
};

// ---------------------------------------------------------------------------
// Backwards-compatible named exports (aliases to logger methods)
// These can be removed once all call-sites use logger.* directly.
// ---------------------------------------------------------------------------
export const consoleLog = (...args) => logger.log(...args);
export const consoleInfo = (...args) => logger.info(...args);
export const consoleWarn = (...args) => logger.warn(...args);
export const consoleError = (...args) => logger.error(...args);
